import { axios } from '~/util/http/axioshttp';
import { useQuery } from '@tanstack/react-query';

import type { FormControlOptionModel } from '../../forms/FormControlOptionModel';
import { GetAutocompleteProps, type AutocompleteOptionType, type AutocompleteTypeToModel } from '@generated';
import type { SieveFilterModel } from '../sieve/filter';
import { buildSieveUrl } from '../sieve/sieve';

interface useAutocompleteProps<K extends AutocompleteOptionType> {
	searchType: K;
	searchTerm: string;
	initialValue?: string | null;
	enabled?: boolean;
	groupResult: boolean;
	additionalFilters?: SieveFilterModel[];
	sortColumn?: string | null;
	sortDirection?: string | null;
}

interface useAutocompleteResult<T> {
	data: T[];
	options: FormControlOptionModel[];
	count: number;
	isLoading: boolean;
	isFetching: boolean;
	isError: boolean;
	getSelectedOption: (selected: string) => FormControlOptionModel;
	getSelectedData: (selected: string) => T;
}

function getFilters(
	labelPropKey: string,
	valuePropKey: string,
	searchTerm: string,
	defaultFilters: SieveFilterModel[],
	additionalFilters?: SieveFilterModel[],
	initialValue?: string | null
): SieveFilterModel[] {
	if (initialValue) {
		return [{ name: valuePropKey, operator: '==', value: initialValue }];
	} else {
		return [{ name: labelPropKey, operator: '@=', value: searchTerm }, ...defaultFilters, ...(additionalFilters ?? [])];
	}
}

export function useAutocomplete<K extends AutocompleteOptionType>(props: useAutocompleteProps<K>): useAutocompleteResult<AutocompleteTypeToModel[K]> {
	const query = GetAutocompleteProps(props.searchType);

	const sortColumn = props.sortColumn && props.sortColumn !== '' ? props.sortColumn : query.defaultSortColumn;
	const sortDirection = props.sortDirection ? props.sortDirection : query.defaultSortDirection ? query.defaultSortDirection : 'asc';

	const filters = getFilters(
		query.labelPropKey as string,
		query.valuePropKey as string,
		props.searchTerm,
		query.defaultFilters,
		props.additionalFilters,
		props.initialValue
	);

	const pageSize = props.initialValue ? 1 : 10;

	const queryString = buildSieveUrl(filters, sortColumn as string, sortDirection as string, pageSize, 1);

	const url = `${query.endpoint}?${queryString}`;

	const { data, isLoading, isError, isFetching } = useQuery<{ collection: AutocompleteTypeToModel[K][]; count: number }>({
		queryFn: async () => {
			const resp = await axios.get(url);
			return resp.data;
		},
		queryKey: [query.endpoint, 'search', queryString],
		enabled: (props.enabled && props.searchTerm?.length >= 3) || Boolean(props.initialValue),
		placeholderData: data => data
	});

	const count = data?.count ?? 0;
	const collection = count > 0 ? data!.collection : [];

	const options =
		count !== 0
			? collection?.map((x: AutocompleteTypeToModel[K]) => ({
					label: x[query.labelPropKey] as string,
					value: x[query.valuePropKey] as string
			  }))
			: [];

	const getSelectedOption = (selected: string) => {
		const selectedOption = options.find(x => x.value.toLowerCase() === selected.toLowerCase());
		if (!selectedOption) {
			throw Error('Unable to determine selected option');
		}
		return selectedOption;
	};

	const getSelectedData = (selected: string) => {
		const selectedData = collection.find(x => x[query.valuePropKey] === selected);
		if (!selectedData) {
			throw Error('Unable to determine selected data');
		}
		return selectedData;
	};

	return {
		data: collection ?? [],
		options:
			count !== 0
				? collection?.map((x: AutocompleteTypeToModel[K]) => ({
						label: x[query.labelPropKey] as string,
						value: x[query.valuePropKey] as string
				  }))
				: [],
		count: count ?? 0,
		isLoading,
		isFetching,
		isError,
		getSelectedOption,
		getSelectedData
	};
}
